import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import ArrowUpRight from 'lucide-react/dist/esm/icons/arrow-up-right.mjs';
import LoaderCircle from 'lucide-react/dist/esm/icons/loader-circle.mjs';
import Pencil from 'lucide-react/dist/esm/icons/pencil.mjs';
import Trash2 from 'lucide-react/dist/esm/icons/trash.mjs';
import { CompactionError } from './client.js';
function activeElement(element) {
    const root = element.getRootNode();
    return root instanceof ShadowRoot ? root.activeElement : element.ownerDocument.activeElement;
}
/** Render beside the app's content, outside sidebar and drawer inert regions. */
export function ConversationContextMenu({ position, chatTitle, returnFocus, onRename, onOpenNewSession, onDelete, onClose, topLayer = false }) {
    const rootRef = useRef(null);
    const mountedRef = useRef(false);
    const busyRef = useRef(false);
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [busy, setBusy] = useState(false);
    const [busyLabel, setBusyLabel] = useState('Working…');
    const [error, setError] = useState('');
    const [compactionUnavailable, setCompactionUnavailable] = useState(false);
    const [layout, setLayout] = useState(null);
    const id = useId();
    const restoreFocus = () => {
        if (returnFocus?.isConnected && returnFocus.getClientRects().length && !returnFocus.closest('[inert]'))
            returnFocus.focus({ preventScroll: true });
    };
    const close = (restore = true) => {
        if (busyRef.current)
            return;
        if (restore)
            restoreFocus();
        closeRef.current();
    };
    useEffect(() => {
        mountedRef.current = true;
        const outside = (event) => {
            const root = rootRef.current;
            if (root && !event.composedPath().includes(root) && !busyRef.current)
                closeRef.current();
        };
        document.addEventListener('pointerdown', outside, true);
        return () => { mountedRef.current = false; document.removeEventListener('pointerdown', outside, true); };
    }, []);
    // Before the placement below measures it: a popover is not rendered until it is shown.
    useLayoutEffect(() => {
        if (!topLayer)
            return;
        const element = rootRef.current;
        if (!element)
            return;
        if (typeof element.showPopover === 'function')
            element.showPopover();
        else
            element.removeAttribute('popover');
        return () => {
            try {
                if (typeof element.hidePopover === 'function' && element.matches(':popover-open'))
                    element.hidePopover();
            }
            catch { /* engines without :popover-open */ }
        };
    }, [topLayer]);
    useLayoutEffect(() => {
        const element = rootRef.current;
        if (!element)
            return;
        const measure = () => {
            const viewport = window.visualViewport;
            const leftEdge = (viewport?.offsetLeft ?? 0) + 8;
            const topEdge = (viewport?.offsetTop ?? 0) + 8;
            const width = Math.max(1, (viewport?.width ?? window.innerWidth) - 16);
            const height = Math.max(1, (viewport?.height ?? window.innerHeight) - 16);
            const bounds = element.getBoundingClientRect();
            const next = {
                position: 'fixed',
                left: Math.max(leftEdge, Math.min(position.x, leftEdge + width - Math.min(bounds.width, width))),
                top: Math.max(topEdge, Math.min(position.y, topEdge + height - Math.min(bounds.height, height))),
                maxWidth: width,
                maxHeight: height,
            };
            setLayout(previous => previous && Object.keys(next).every(key => previous[key] === next[key]) ? previous : next);
        };
        measure();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : undefined;
        observer?.observe(element);
        window.addEventListener('resize', measure);
        window.visualViewport?.addEventListener('resize', measure);
        window.visualViewport?.addEventListener('scroll', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
            window.visualViewport?.removeEventListener('resize', measure);
            window.visualViewport?.removeEventListener('scroll', measure);
        };
    }, [position.x, position.y]);
    const positioned = layout !== null;
    useLayoutEffect(() => {
        if (!positioned)
            return;
        // The confirmation's first button is Cancel, making accidental Enter harmless.
        rootRef.current?.querySelector('button')?.focus({ preventScroll: true });
    }, [positioned, confirmDelete]);
    const run = async (action, failure, label = 'Working…') => {
        if (busyRef.current)
            return;
        busyRef.current = true;
        setBusy(true);
        setBusyLabel(label);
        setError('');
        try {
            const ok = await action();
            if (!mountedRef.current)
                return;
            if (ok) {
                const root = rootRef.current;
                // A successful action may already have focused a rename input or a new composer.
                if (root && root.contains(activeElement(root)))
                    restoreFocus();
                closeRef.current();
            }
            else
                setError(failure);
        }
        catch (error) {
            if (mountedRef.current) {
                setError(error instanceof CompactionError ? error.message : failure);
                if (error instanceof CompactionError && error.code === 'summary_not_supported')
                    setCompactionUnavailable(true);
            }
        }
        finally {
            busyRef.current = false;
            if (mountedRef.current)
                setBusy(false);
        }
    };
    const keydown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            // Escape always gets the user out, even while an action is still running (a slow save or summary); the action
            // finishes in the background and its result lands in the chat list.
            if (busyRef.current) {
                restoreFocus();
                closeRef.current();
                return;
            }
            if (confirmDelete) {
                setConfirmDelete(false);
                setError('');
            }
            else
                close();
            return;
        }
        const root = rootRef.current;
        if (!root)
            return;
        const buttons = Array.from(root.querySelectorAll('button'));
        if (event.key === 'Tab') {
            event.preventDefault();
            event.stopPropagation();
            if (busyRef.current)
                return;
            if (!confirmDelete) {
                close();
                return;
            }
            const current = buttons.indexOf(activeElement(root));
            buttons[(current + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length]?.focus();
            return;
        }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key))
            return;
        event.preventDefault();
        event.stopPropagation();
        if (busyRef.current)
            return;
        const current = buttons.indexOf(activeElement(root));
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
            : (current + (event.key === 'ArrowUp' ? -1 : 1) + buttons.length) % buttons.length;
        buttons[index]?.focus();
    };
    return _jsxs("div", { ref: rootRef, popover: topLayer ? 'manual' : undefined, className: "gyro-chat-context-menu", style: { ...layout, visibility: positioned ? 'visible' : 'hidden' }, role: confirmDelete ? 'alertdialog' : 'menu', "aria-label": confirmDelete ? undefined : `Actions for ${chatTitle}`, "aria-labelledby": confirmDelete ? `${id}-title` : undefined, "aria-describedby": confirmDelete ? `${id}-description` : undefined, "aria-busy": busy || undefined, onKeyDown: keydown, onContextMenu: event => event.preventDefault(), children: [confirmDelete ? _jsxs(_Fragment, { children: [_jsx("h3", { id: `${id}-title`, className: "gyro-chat-context-title", children: "Delete chat?" }), _jsxs("p", { id: `${id}-description`, className: "gyro-chat-context-description", children: ["Delete \u201C", chatTitle, "\u201D from your chat history? This cannot be undone."] }), _jsxs("div", { className: "gyro-chat-context-confirm", children: [_jsx("button", { type: "button", className: "gyro-chat-context-button", "aria-disabled": busy || undefined, onClick: () => { if (!busyRef.current) {
                                    setConfirmDelete(false);
                                    setError('');
                                } }, children: "Cancel" }), _jsxs("button", { type: "button", className: "gyro-chat-context-button is-destructive", "aria-disabled": busy || undefined, onClick: () => void run(onDelete, 'Could not delete this chat. Check your connection and try again.'), children: [_jsx(Trash2, { size: 15, "aria-hidden": "true" }), "Delete chat"] })] })] }) : _jsxs(_Fragment, { children: [_jsxs("button", { type: "button", role: "menuitem", className: "gyro-chat-context-item", "aria-disabled": busy || undefined, onClick: () => void run(onRename, 'Could not open chat renaming. Try again.'), children: [_jsx(Pencil, { size: 16, "aria-hidden": "true" }), _jsx("span", { children: "Rename" })] }), _jsxs("button", { type: "button", role: "menuitem", className: "gyro-chat-context-item", "aria-disabled": busy || compactionUnavailable || undefined, "data-unavailable": compactionUnavailable || undefined, title: compactionUnavailable ? 'Service update required' : 'Compact this chat into a summary and continue in a fresh context', onClick: () => { if (!compactionUnavailable)
                            void run(onOpenNewSession, 'Could not compact this chat into a new session. Check your connection and try again.', 'Compacting chat…'); }, children: [_jsx(ArrowUpRight, { size: 16, "aria-hidden": "true" }), _jsx("span", { children: "Open in a new session" })] }), _jsx("div", { className: "gyro-chat-context-divider", role: "separator" }), _jsxs("button", { type: "button", role: "menuitem", className: "gyro-chat-context-item is-destructive", "aria-disabled": busy || undefined, onClick: () => { if (!busyRef.current) {
                            setConfirmDelete(true);
                            setError('');
                        } }, children: [_jsx(Trash2, { size: 16, "aria-hidden": "true" }), _jsx("span", { children: "Delete chat" })] })] }), busy && _jsxs("p", { className: "gyro-chat-context-status", role: "status", children: [_jsx(LoaderCircle, { size: 14, className: "gyro-file-spinner", "aria-hidden": "true" }), busyLabel] }), error && _jsx("p", { className: "gyro-chat-context-error", role: "alert", children: error })] });
}
//# sourceMappingURL=ConversationContextMenu.js.map